import { useState } from 'react';
import { Icon, Link } from '@nimbus-ds/components';
import { ChevronLeftIcon } from '@nimbus-ds/icons';
import { SideModal } from '@nimbus-ds/patterns';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import { AddDomainOptions, SearchNewDomain } from './steps';
import { DomainModalStep } from './types';

interface AddDomainModalProps {
  onClose: () => void;
  onOpenAddExistingDomain?: () => void;
  initialSearchQuery?: string;
  hasActiveProcessingOrder?: boolean;
  onRedirectToActiveOrder?: () => void;
}

const DEFAULT_STEP: DomainModalStep = 'addDomainOptions';

export function AddDomainModal({
  onClose,
  onOpenAddExistingDomain,
  initialSearchQuery,
  hasActiveProcessingOrder = false,
  onRedirectToActiveOrder,
}: Readonly<AddDomainModalProps>) {
  const t = useTranslationConfigurations('domains.landing');
  const initialStep =
    initialSearchQuery !== undefined ? 'searchNewDomain' : DEFAULT_STEP;
  const [currentStep, setCurrentStep] = useState<DomainModalStep>(initialStep);

  const handleChangeStep = (step: DomainModalStep) => {
    if (
      step === 'searchNewDomain' &&
      hasActiveProcessingOrder &&
      onRedirectToActiveOrder
    ) {
      onClose();
      onRedirectToActiveOrder();
      return;
    }
    setCurrentStep(step);
  };

  const handleClose = () => {
    setCurrentStep(DEFAULT_STEP);
    onClose();
  };

  const handleBack = () => {
    if (currentStep === DEFAULT_STEP) {
      handleClose();
    } else {
      setCurrentStep(DEFAULT_STEP);
    }
  };

  const getStepTitle = () => t(`addDomainModal.titles.${currentStep}`);

  const renderStep = () => {
    switch (currentStep) {
      case 'addDomainOptions':
        return (
          <AddDomainOptions
            onChangeStep={handleChangeStep}
            onClose={handleClose}
            onOpenAddExistingDomain={onOpenAddExistingDomain}
          />
        );
      case 'searchNewDomain':
        return <SearchNewDomain initialSearchQuery={initialSearchQuery} />;
      default:
        return (
          <AddDomainOptions
            onChangeStep={handleChangeStep}
            onClose={handleClose}
            onOpenAddExistingDomain={onOpenAddExistingDomain}
          />
        );
    }
  };

  return (
    <SideModal
      open
      onRemove={handleClose}
      maxWidth={{ xs: '100%', md: '540px' }}
      paddingBody="none"
      headerAction={
        <Link onClick={handleBack} textDecoration="none">
          <Icon color="primary-textHigh" source={<ChevronLeftIcon />} />
        </Link>
      }
      title={getStepTitle()}
    >
      {renderStep()}
    </SideModal>
  );
}
